// @ts-strict-ignore
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ArticleTechnicalInfoVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleTechnicalInfoManager } from '@article/manager/article-technical-info.manager';
import { FloatRangeVO, PropertyInLocalesVO, PropertyVO } from '../../article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { LocalizedPropertyService } from '@shared/localization/localized-property.service';
import { UserModel } from '@user/model/user.model';
import { NumberUtils } from '@shared/util/number-utils';
import { Subscription } from 'rxjs/internal/Subscription';
import { ArticleTechnicalInfoNotification, ArticleTechnicalInfoService } from '@article/service/article-technical-info.service';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Component({
  standalone: false,
  selector: 'app-wire-bridge-info',
  templateUrl: './wire-bridge-info.component.html',
})
export class WireBridgeInfoComponent implements OnInit, OnDestroy {

  public articleUsage: ArticleUsageVO;

  public form: FormGroup;

  public hasEditPermission: boolean;

  private _article: ArticleVO;

  private localeChangedSubscription: Subscription;
  private articleTechnicalInfoSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private technicalInfoManager: ArticleTechnicalInfoManager,
    private localeResourceManager: LocaleResourceManager,
    private getLocalizedPropertyService: LocalizedPropertyService,
    private articleTechnicalInfoService: ArticleTechnicalInfoService,
    private articleUsageService: ArticleUsageService
  ) {}

  public ngOnInit(): void {
    this.localeChangedSubscription = this.localeResourceManager.localeChanged$.subscribe(() => this.onLocaleChange());
    this.articleTechnicalInfoSubscription =
      this.articleTechnicalInfoService.articleTechnicalInfo$.subscribe((notification:ArticleTechnicalInfoNotification) => {
        if (notification.success) {
          this.technicalInfoLoaded();
        }
      });

    this.hasEditPermission = this.userModel.user.hasPermission("EDIT_PROJECT");

    this.form = this.formBuilder.group({
      crossSection: [''],
      cableLength: [this.getCableLength(this.articleUsage)],
      color: [''],
    });

    this.form.controls.crossSection.disable();
    if (!this.hasEditPermission) {
      this.form.controls.cableLength.disable();
    }
    this.form.controls.color.disable();

    this._article = this.articleUsageService.getArticleByArticleUsage(this.articleUsage);
    if (!ArticleUtils.isWireBridgeArticle(this._article)) {
      return;
    }

    this.setTechnicalInfo();
  }

  public ngOnDestroy(): void {
    this.localeChangedSubscription.unsubscribe();
    this.articleTechnicalInfoSubscription.unsubscribe();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): void {
    const newLengthValue: number = Number(this.form.controls.cableLength.value);
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(this.articleUsage, ArticleUsageInfoPropertyDef.CABLE_LENGTH, newLengthValue);
    this.close();
  }

  public getCableLength(articleUsage: ArticleUsageVO): number {
    const result: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.CABLE_LENGTH);
    return NumberUtils.isNotNumber(result) ? 0 : result;
  }

  private setTechnicalInfo(): void {
    if (!this.articleUsage) {
      return;
    }
    const technicalInfo: ArticleTechnicalInfoVO = this.technicalInfoManager.getTechnicalInfo(this.articleUsage.articleId);
    if (!technicalInfo) {
      this.technicalInfoManager.loadTechnicalInfoByArticleIds([this.articleUsage.articleId]);
    } else {
      this.articleTechnicalInfoService.notifyArticleTechnicalInfoLoadedSuccess([technicalInfo]);
    }
  }

  private technicalInfoLoaded(): void {
    if (!this._article || !this.articleUsage) {
      return;
    }
    const technicalInfo: ArticleTechnicalInfoVO = this.technicalInfoManager.getTechnicalInfo(this.articleUsage.articleId);
    this.showColorInfo(this.articleUsage.articleId);

    let articleCrossSectionRange: FloatRangeVO = null;
    if (technicalInfo) {
      articleCrossSectionRange = this.createCrossSectionRangeByTechnicalInfo(technicalInfo.articleTechnicalInfoVOs);
    }
    if (!articleCrossSectionRange) {
      articleCrossSectionRange = this.createCrossSectionRangeByArticleProperty(this._article.connectionFeatures);
    }
    this.form.controls.crossSection.setValue(this.getCrossSectionLabel(articleCrossSectionRange, null));
  }

  private showColorInfo(articleId: number): void {
    this.form.controls.color.setValue(
      this.getLocalizedPropertyService.getColorNameFromArticleId(articleId)
    );
  }

  private createCrossSectionRangeByTechnicalInfo(articleTechnicalInfoVOs: PropertyInLocalesVO[]): FloatRangeVO {
    const fromCrossSection: number = this.getLocalizedPropertyService.floatValue(
      articleTechnicalInfoVOs,
      ArticleTechnicalInfoPropertyDef.CROSS_SECTION_FROM_1_MM
    );
    const upToCrossSection: number = this.getLocalizedPropertyService.floatValue(
      articleTechnicalInfoVOs,
      ArticleTechnicalInfoPropertyDef.CROSS_SECTION_UP_TO_1_MM
    );
    let crossSectionRange: FloatRangeVO = null;
    if (NumberUtils.isNumber(fromCrossSection) || NumberUtils.isNumber(upToCrossSection)) {
      crossSectionRange = new FloatRangeVO();
      crossSectionRange.left = fromCrossSection;
      crossSectionRange.right = upToCrossSection;
    }
    return crossSectionRange;
  }

  private createCrossSectionRangeByArticleProperty(articleProperties: PropertyVO[]): FloatRangeVO {
    const crossSection: number = PropertyUtils.getFloatPropertyValue(articleProperties, ArticleConnectionPropertyDef.CROSS_SECTION);
    let crossSectionRange: FloatRangeVO = null;
    if (NumberUtils.isNumber(crossSection)) {
      crossSectionRange = new FloatRangeVO();
      crossSectionRange.left = crossSection;
    }
    return crossSectionRange;
  }

  public getCrossSectionLabel(crossSection: FloatRangeVO, numberFormat: Function): string {
    if (!crossSection) {
      return '';
    }
    let crossSectionLabel: string = '';
    if (NumberUtils.isNumber(crossSection.left)) {
      crossSectionLabel += !numberFormat ? crossSection.left.toString() : numberFormat(crossSection.left, false);
    }
    if (NumberUtils.isNumber(crossSection.right)) {
      if (crossSectionLabel.length > 0) {
        crossSectionLabel += ' - ';
      }
      crossSectionLabel += !numberFormat ? crossSection.right.toString() : numberFormat(crossSection.right, false);
    }
    return crossSectionLabel;
  }

  private onLocaleChange(): void {
    if (this._article) {
      this.showColorInfo(this._article.identity);
    }
  }
}
